import { useState, useEffect, useRef } from "react";

import { useCrimeSceneStore } from "src/store/crimeSceneStore";
import Image from "@/components/image";
import { CRIME_MC_QUESTIONS, ALL_SCENES_DATA } from "src/const/crimeSceneConst";
import Text from "@/components/text/text";
import { MCOptions, MCQuestions } from "@/types/crime";

import useMdBreakpoint from "@/hooks/useMdBreakpoint";
import AIAgentButton from "@/components/button/ai-agent-button";
import SimulationScore from "./sim-score";

interface SimMCPanelProps {
  currentScene: number;
}

export default function SimMCPanel(props: SimMCPanelProps) {
  const { currentScene } = props;
  const isMd = useMdBreakpoint();
  const contentRef = useRef<HTMLDivElement>(null);

  // for simulation
  const setCrimeMCQuestions = useCrimeSceneStore(
    (state) => state.setCrimeMCQuestions
  );
  const setShowMCPanel = useCrimeSceneStore((state) => state.setShowMCPanel);
  const [sceneMCQ, setSceneMCQ] = useState<MCQuestions[]>([]);
  const [mcqIndex, setMcqIndex] = useState(0);

  const addSceneScore = useCrimeSceneStore((state) => state.addSceneScore);
  const setProgress = useCrimeSceneStore((state) => state.setProgress);
  const startAutoPlayDelay = useCrimeSceneStore(
    (state) => state.startAutoPlayDelay
  );
  const sceneIndex = useCrimeSceneStore((state) => state.sceneIndex);
  const setSceneIndex = useCrimeSceneStore((state) => state.setSceneIndex);
  const startPlayMessages = useCrimeSceneStore(
    (state) => state.startPlayMessages
  );

  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [score, setScore] = useState(0);
  const isFinishAllMc = useCrimeSceneStore((state) => state.isFinishedAllMc);

  const setIsFinishedAllMc = useCrimeSceneStore(
    (state) => state.setIsFinishedAllMc
  );

  useEffect(() => {
    const currentSceneMCQ = CRIME_MC_QUESTIONS.filter(
      (c) => c.scene === currentScene
    );
    setCrimeMCQuestions(CRIME_MC_QUESTIONS);
    setSceneMCQ(currentSceneMCQ);
  }, [isMd]);

  const setIsShownIncidentOccurence = useCrimeSceneStore(
    (state) => state.setIsShownIncidentOccurence
  );

  const mCQuestion = (question: string) => {
    return (
      <div className="flex gap-2 items-center mb-[20px]">
        <Image
          src="/assets/icon_ai_agent.png"
          alt="agent button"
          width={22}
          height={22}
        />
        <Text text={question} className="text-500" size={20} />
      </div>
    );
  };

  const checkAnswer = (answer: string) => {
    if (!selectedAnswer) {
      setSelectedAnswer(answer);
      if (answer === sceneMCQ[mcqIndex].correct) {
        setScore((prev) => prev + 1);
      }
    }
  };

  const crimeSceneOptionsContent = (options: MCOptions[]) => {
    return options.map((option: MCOptions, index) => (
      <div
        className={`mc-item
        ${
          selectedAnswer !== "" && option.choice === sceneMCQ[mcqIndex].correct
            ? "selected"
            : ""
        }
       ${
         selectedAnswer !== "" &&
         option.choice === selectedAnswer &&
         option.choice !== sceneMCQ[mcqIndex].correct
           ? "incorrect"
           : ""
       }
        ${selectedAnswer !== "" ? "mc-item-disable" : ""}
        `}
        key={`${option.choice}-${index}`}
        onClick={() => checkAnswer(option.choice)}
      >
        <div className="flex items-start gap-1">
          {/* ${option.choice})  */}
          <Text
            text={`${option.choice})`}
            size={18}
            className={`mc-item-text ${
              selectedAnswer !== "" &&
              option.choice === sceneMCQ[mcqIndex].correct
                ? "primary-text-dark"
                : "primary-text"
            }`}
          />
          <Text
            text={`${option.answer}`}
            size={18}
            className={`mc-item-text ${
              selectedAnswer !== "" &&
              option.choice === sceneMCQ[mcqIndex].correct
                ? "primary-text-dark"
                : "primary-text"
            }`}
          ></Text>
        </div>
      </div>
    ));
  };

  const replay = () => {
    setShowMCPanel(false);
    setProgress(0);
    startAutoPlayDelay(0);
    startPlayMessages();
  };

  const onNext = () => {
    if (mcqIndex + 1 < sceneMCQ.length) {
      setSelectedAnswer("");
      setMcqIndex(mcqIndex + 1);
    } else {
      // set total score earned in this scene
      addSceneScore(score);
      setScore(0);
      if (sceneIndex < ALL_SCENES_DATA.length - 1) {
        setShowMCPanel(false);
        setSceneIndex(sceneIndex + 1);
      } else {
        setIsFinishedAllMc(true);
      }
    }
  };

  const ButtonControls = () => {
    return (
      <>
        {selectedAnswer !== "" ? (
          <div className="flex md:justify-center">
            <button
              className="btn-style primary-btn w-full md:max-w-[280px]"
              onClick={() => onNext()}
            >
              <Text text="btn_next" />
            </button>
          </div>
        ) : (
          <div className="flex justify-between mt-[12px]">
            <AIAgentButton
              onClick={() => {
                setIsShownIncidentOccurence(true);
              }}
            />

            <div
              className="flex items-center gap-2 btn-style px-[20px]"
              onClick={() => replay()}
            >
              <Image
                src="/assets/icon_replay.png"
                alt="icon"
                width={22}
                height={22}
              />

              <Text text="replay" />
            </div>
          </div>
        )}
      </>
    );
  };

  const answerExplanation = (answer: string, explanation: string) => {
    return (
      <div className="mt-[20px] grid gap-3">
        <Text
          className="text-500"
          text={selectedAnswer === answer ? "correct" : "incorrect"}
          size={24}
        />
        <div className="flex">
          <Text text="correct_answer" className="text-500" size={18} />
          <Text text={` ${answer}`} className="text-500" size={18} />
        </div>

        <Text text={explanation} size={18} />
      </div>
    );
  };

  const MultipleChoiceContent = () => {
    return (
      <>
        {sceneMCQ.length > 0 && (
          <div
            ref={contentRef}
            className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {mCQuestion(sceneMCQ[mcqIndex].question)}
            {crimeSceneOptionsContent(sceneMCQ[mcqIndex].options)}
            {selectedAnswer !== "" &&
              answerExplanation(
                sceneMCQ[mcqIndex].correct,
                sceneMCQ[mcqIndex].explanation
              )}
          </div>
        )}

        <ButtonControls />
      </>
    );
  };

  return (
    <>
      <div className="fixed top-0 w-full h-full bg-overlay z-[51]"></div>
      <div
        className={`transition-all transition-transform duration-300 ease-in-out
        fixed left-0 right-0 bottom-0 w-full h-full
        ${isMd ? "flex justify-center items-center translate-y-0" : ""}`}
        style={{
          zIndex: 51,
        }}
      >
        <div
          className={`flex flex-col ${
            isFinishAllMc
              ? "w-full h-full"
              : "h-full py-[24px] px-[20px] md:rounded-xl md:max-h-[600px]"
          }`}
          style={{
            background: "linear-gradient(270deg, #04290C 0%, #022C30 100%)",
          }}
        >
          {isFinishAllMc ? (
            <SimulationScore />
          ) : (
            <div className="flex flex-col h-full md:w-[600px]">
              <MultipleChoiceContent />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
